var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g;
    return g = { next: verb(0), "throw": verb(1), "return": verb(2) }, typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (_) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { useEffect, useCallback, useReducer, useMemo, useRef } from "react";
import { useRequest } from "./useRequest";
import { useDeepMemo, useMountedState } from "./utils";
var REQUEST_CLEAR_MESSAGE = "A new request has been made before completing the last one";
function getNextState(state, action) {
    return {
        data: action.type === "success" ? action.data : state.data,
        other: action.type === "success" ? action.other : state.other,
        error: action.type === "error" ? action.error : undefined,
        isLoading: action.type === "start" ? true : false,
    };
}
export function useResource(fn, requestParams) {
    var _this = this;
    var getMountedState = useMountedState();
    var _a = useRequest(fn), createRequest = _a[0], clear = _a[1].clear;
    var _b = useReducer(getNextState, {
        isLoading: Boolean(requestParams),
    }), state = _b[0], dispatch = _b[1];
    var request = useCallback(function () {
        var args = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            args[_i] = arguments[_i];
        }
        clear(REQUEST_CLEAR_MESSAGE);
        var _a = createRequest.apply(void 0, args), ready = _a.ready, cancel = _a.cancel;
        void (function () { return __awaiter(_this, void 0, void 0, function () {
            var _a, data, other, e_1, error;
            return __generator(this, function (_b) {
                switch (_b.label) {
                    case 0:
                        _b.trys.push([0, 2, , 3]);
                        getMountedState() && dispatch({ type: "start" });
                        return [4 /*yield*/, ready()];
                    case 1:
                        _a = _b.sent(), data = _a[0], other = _a[1];
                        getMountedState() && dispatch({ type: "success", data: data, other: other });
                        return [3 /*break*/, 3];
                    case 2:
                        e_1 = _b.sent();
                        error = e_1;
                        if (getMountedState() && !error.isCancel) {
                            dispatch({ type: "error", error: error });
                        }
                        return [3 /*break*/, 3];
                    case 3: return [2 /*return*/];
                }
            });
        }); })();
        return cancel;
    }, [clear, createRequest, getMountedState]);
    var requestRefFn = useRef(request);
    useEffect(function () {
        requestRefFn.current = request;
    }, [request]);
    useEffect(function () {
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        var canceller = function () { };
        if (requestParams) {
            canceller = requestRefFn.current.apply(requestRefFn, requestParams);
        }
        return canceller;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, useDeepMemo([requestParams]));
    return useMemo(function () {
        var cancel = function (message) {
            getMountedState() && dispatch({ type: "reset" });
            clear(message);
        };
        var result = [__assign(__assign({}, state), { cancel: cancel }), request];
        return result;
    }, [state, request, getMountedState, clear]);
}
